import React, { useEffect, useState } from 'react';

const Schedule = () => {
  // Helper function to get current date in PST
  const getCurrentPSTDate = () => {
    const now = new Date();
    return new Date(now.toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
  };

  // Helper function to create event schema
  const createEventSchema = (name, startDate, endDate, location, description) => ({
    "@context": "https://schema.org",
    "@type": "Event",
    "name": name,
    "startDate": startDate,
    "endDate": endDate,
    "location": {
      "@type": "Place",
      "name": location.name,
      "address": {
        "@type": "PostalAddress",
        "streetAddress": location.street,
        "addressLocality": "Vancouver",
        "addressRegion": "BC",
        "postalCode": location.postalCode,
        "addressCountry": "CA"
      }
    },
    "description": description,
    "eventStatus": "https://schema.org/EventScheduled",
    "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
    "offers": {
      "@type": "Offer",
      "availability": "https://schema.org/InStock",
      "price": "10",
      "priceCurrency": "CAD",
      "validFrom": "2025-01-21T12:00"
    },
    "organizer": {
      "@type": "Organization",
      "name": "Rueda de la Vida Dance Society",
      "url": "https://ruedadance.org"
    },      
    "image": [
      "https://ruedadance.org/static/media/dance-hero.e31be86049f35cf92066.jpg",
     ]
  });

  // Helper function to generate dates for recurring events
  const generateRecurringDates = (dayOfWeek, startHour, endHour, monthsAhead = 3) => {
    const dates = [];
    const today = getCurrentPSTDate();
    const endDate = new Date(today);
    endDate.setMonth(today.getMonth() + monthsAhead);
    
    let currentDate = new Date(today);
    
    while (currentDate <= endDate) {
      if (currentDate.getDay() === dayOfWeek) {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth() + 1;
        const day = currentDate.getDate();
        
        const startDate = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T${String(startHour).padStart(2, '0')}:${String(startHour % 1 * 60).padStart(2, '0')}`;
        const endDate = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T${String(endHour).padStart(2, '0')}:${String(endHour % 1 * 60).padStart(2, '0')}`;
        
        dates.push({ startDate, endDate, day, month, year });
      }
      
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return dates;
  };

  // Location objects
  const locations = {
    broadway: {
      name: "Broadway Location",
      street: "345 W Broadway",
      postalCode: "V5Y 1P8"
    },
    sixth: {
      name: "6th Ave Location",
      street: "76 W 6th Ave",
      postalCode: "V5Y 1K1"
    }
  };

  // Check if current date is May 30th, 2025 or if we should show June schedule
  const currentDate = getCurrentPSTDate();
  const isMay30th = currentDate.getMonth() === 4 && currentDate.getDate() === 30 && currentDate.getFullYear() === 2025;
  const isJuneSchedule = (currentDate.getMonth() === 4 && currentDate.getDate() >= 31) || currentDate.getMonth() >= 5; // May 31st onwards or June+

  // Generate schema for all events
  const sundayBachataDates = generateRecurringDates(0, 18, 19); // Sunday 6-7PM
  const sundaySalsaDates = generateRecurringDates(0, 19, 20); // Sunday 7-8PM
  const sundaySocialDates = generateRecurringDates(0, 20, 22); // Sunday 8-10PM
  
  const mondaySalsaDates = generateRecurringDates(1, 18.25, 19.25); // Monday 6:15-7:15PM
  const mondayBachataDates = generateRecurringDates(1, 19.25, 20.25); // Monday 7:15-8:15PM
  const mondayAdvancedDates = generateRecurringDates(1, 20.25, 21.25); // Monday 8:15-9:15PM
  
  const fridayBachataDates = generateRecurringDates(5, 19, 20); // Friday 7-8PM
  const fridaySalsaDates = generateRecurringDates(5, 20, 21); // Friday 8-9PM
  const fridaySocialDates = generateRecurringDates(5, 21, 22); // Friday 9-10PM

  // Helper function to get Friday location (special case for May 30th)
  const getFridayLocation = (dateString) => {
    // Check if the date string represents May 30th, 2025
    if (dateString && dateString.includes('2025-05-30')) {
      return locations.sixth; // Use 6th Ave location for May 30th
    }
    return locations.broadway; // Default Friday location
  };

  // Include all regular classes in the schema
  const eventsSchema = [
    
    // Sunday Events
    ...sundayBachataDates.map(({ startDate, endDate }) =>
      createEventSchema(
        "Beginners/Improvers Bachata Class",
        startDate,
        endDate,
        locations.broadway,
        "Learn the basics of Bachata dancing in this beginner-friendly class"
      )
    ),
    ...sundaySalsaDates.map(({ startDate, endDate }) =>
      createEventSchema(
        "Beginners/Improvers Salsa Class",
        startDate,
        endDate,
        locations.broadway,
        "Introduction to Salsa dancing for beginners"
      )
    ),
    ...sundaySocialDates.map(({ startDate, endDate }) =>
      createEventSchema(
        "Sunday Social Dance",
        startDate,
        endDate,
        locations.broadway,
        "Social dancing event featuring Salsa and Bachata music"
      )
    ),
    
    // Monday Events
    ...mondaySalsaDates.map(({ startDate, endDate }) =>
      createEventSchema(
        "Intermediate Salsa Class",
        startDate,
        endDate,
        locations.sixth,
        "Intermediate level Salsa class for experienced dancers"
      )
    ),
    ...mondayBachataDates.map(({ startDate, endDate }) =>
      createEventSchema(
        "Intermediate Bachata Class",
        startDate,
        endDate,
        locations.sixth,
        "Intermediate level Bachata class for experienced dancers"
      )
    ),
    ...mondayAdvancedDates.map(({ startDate, endDate }) =>
      createEventSchema(
        "Advanced Salsa Class",
        startDate,
        endDate,
        locations.sixth,
        "Advanced Salsa class for experienced dancers"
      )
    ),
    
    // Friday Events (with special handling for May 30th)
    ...fridayBachataDates.map(({ startDate, endDate }) =>
      createEventSchema(
        "Beginners/Improvers Bachata Class",
        startDate,
        endDate,
        getFridayLocation(startDate),
        "Learn the basics of Bachata dancing in this beginner-friendly class"
      )
    ),
    ...fridaySalsaDates.map(({ startDate, endDate }) =>
      createEventSchema(
        "Beginners/Improvers Salsa Class",
        startDate,
        endDate,
        getFridayLocation(startDate),
        "Introduction to Salsa dancing for beginners"
      )
    ),
    ...fridaySocialDates.map(({ startDate, endDate }) =>
      createEventSchema(
        "Friday Social Dance",
        startDate,
        endDate,
        getFridayLocation(startDate),
        "Social dancing event featuring Salsa and Bachata music"
      )
    )
  ];


  const memoizedEventsSchema = React.useMemo(() => eventsSchema, [isJuneSchedule]);

  useEffect(() => {
    // Remove any existing schema script
    const existingScript = document.getElementById('event-schema');
    if (existingScript) {
      existingScript.remove();
    }

    // Create and append new schema script
    const script = document.createElement('script');
    script.id = 'event-schema';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(memoizedEventsSchema);
    document.head.appendChild(script);

    // Cleanup on unmount
    return () => {
      const script = document.getElementById('event-schema');
      if (script) {
        script.remove();
      }
    };
  }, [memoizedEventsSchema]);

  const currentMonth = getCurrentPSTDate().toLocaleString('en-US', { month: 'long' }).toUpperCase();

  return (
    <section id="schedule" className="py-24 px-4 bg-black text-white">
      <div className="max-w-5xl mx-auto">
        <h2 className="text-4xl font-bold text-white text-center mb-4">{currentMonth}</h2>
        
        {/* Special Notice for May 30th */}
        {isMay30th && (
          <div className="bg-primary text-white p-4 rounded-lg mb-8 text-center">
            <p className="font-bold text-lg">📍 SPECIAL LOCATION CHANGE TODAY!</p>
            <p>Friday classes (May 30th) are at <strong>76 W 6th Ave</strong> instead of the usual Broadway location.</p>
          </div>
        )}

        {/* June Schedule Notice */}
        {isJuneSchedule && (
          <div className="bg-green-600 text-white p-4 rounded-lg mb-8 text-center">
            <p className="font-bold text-lg">🌟 NEW JUNE SCHEDULE!</p>
            <p>Updated class times and locations - see below for details</p>
          </div>
        )}
        
        {/* Conditional Schedule Display */}
        {isJuneSchedule ? (
          /* June Schedule */
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Sunday Schedule - June */}
            <div className="bg-light-gray p-6 rounded-lg shadow-lg">
              <h3 className="text-2xl font-bold text-primary mb-4">SUNDAY</h3>
              <div className="mb-4">
                <p className="text-sm text-secondary mb-2">345 W BROADWAY,</p>
                <p className="text-sm text-secondary mb-4">VANCOUVER, V5Y 1P8</p>
              </div>
              
              <div className="space-y-4">
                <div className="bg-background text-white p-3 rounded-full">
                  <p className="font-bold">BEGINNER/IMPROVER BACHATA</p>
                  <p>7-8PM</p>
                </div>
                <div className="bg-background text-white p-3 rounded-full">
                  <p className="font-bold">BEGINNER/IMPROVER SALSA</p>
                  <p>8-9PM</p>
                </div>
                <div className="bg-primary text-white p-3 rounded-full">
                  <p className="font-bold">SALSA & BACHATA SOCIAL</p>
                  <p>9-10PM</p>
                </div>
              </div>
            </div>

            {/* Monday Schedule - June */}
            <div className="bg-light-gray p-6 rounded-lg shadow-lg">
              <h3 className="text-2xl font-bold text-primary mb-4">MONDAY</h3>
              <div className="mb-4">
                <p className="text-sm text-secondary mb-2">76 W 6TH AVE,</p>
                <p className="text-sm text-secondary mb-4">VANCOUVER, V5Y 1K1</p>
              </div>
              
              <div className="space-y-4">
                <div className="bg-background text-white p-3 rounded-full">
                  <p className="font-bold">INTERMEDIATE SALSA</p>
                  <p>6:15-7:15PM</p>
                </div>
                <div className="bg-background text-white p-3 rounded-full">
                  <p className="font-bold">INTERMEDIATE BACHATA</p>
                  <p>7:15-8:15PM</p>
                </div>
                <div className="bg-background text-white p-3 rounded-full">
                  <p className="font-bold">ADVANCED SALSA</p>
                  <p>8:15-9:15PM</p>
                </div>
              </div>
            </div>

            {/* Friday Schedule - June */}
            <div className="bg-light-gray p-6 rounded-lg shadow-lg">
              <h3 className="text-2xl font-bold text-primary mb-4">FRIDAY</h3>
              <div className="mb-4">
                <div className="bg-green-600 text-white px-2 py-1 rounded text-xs font-bold mb-2 inline-block">
                  FREE COMMUNITY EVENT
                </div>
                <p className="text-sm text-secondary mb-2">SUN HOP PARK,</p>
                <p className="text-sm text-secondary mb-4">3333 MAIN ST, VANCOUVER</p>
              </div>
              
              <div className="space-y-4">
                <div className="bg-background text-white p-3 rounded-full">
                  <p className="font-bold">SALSA, BACHATA, MERENGUE</p>
                  <p className="text-sm">Intro Lesson: 7:00PM</p>
                </div>
                <div className="bg-primary text-white p-3 rounded-full">
                  <p className="font-bold">SOCIAL DANCING</p>
                  <p className="text-sm">7:30-9:00PM</p>
                </div>
                <div className="bg-green-600 text-white p-3 rounded-full">
                  <p className="font-bold">🎉 FREE EVENT!</p>
                  <p className="text-sm">All Welcome</p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Regular May Schedule */
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Sunday Schedule */}
            <div className="bg-light-gray p-6 rounded-lg shadow-lg">
              <h3 className="text-2xl font-bold text-primary mb-4">SUNDAY</h3>
              <div className="mb-4">
                <p className="text-sm text-secondary mb-2">345 W BROADWAY,</p>
                <p className="text-sm text-secondary mb-4">VANCOUVER, V5Y 1P8</p>
              </div>
              
              <div className="space-y-4">
                <div className="bg-background text-white p-3 rounded-full">
                  <p className="font-bold">BEGINNERS/IMPROVERS BACHATA</p>
                  <p>6-7PM</p>
                </div>
                <div className="bg-background text-white p-3 rounded-full">
                  <p className="font-bold">BEGINNERS/IMPROVERS SALSA</p>
                  <p>7-8PM</p>
                </div>
                <div className="bg-primary text-white p-3 rounded-full">
                  <p className="font-bold">SUNDAY SOCIAL</p>
                  <p>8-10PM</p>
                </div>
              </div>
            </div>

            {/* Monday Schedule */}
            <div className="bg-light-gray p-6 rounded-lg shadow-lg">
              <h3 className="text-2xl font-bold text-primary mb-4">MONDAY</h3>
              <div className="mb-4">
                <p className="text-sm text-secondary mb-2">76 W 6TH AVE,</p>
                <p className="text-sm text-secondary mb-4">VANCOUVER, V5Y 1K1</p>
              </div>
              
              <div className="space-y-4">
                <div className="bg-background text-white p-3 rounded-full">
                  <p className="font-bold">INTERMEDIATE SALSA</p>
                  <p>6:15-7:15PM</p>
                </div>
                <div className="bg-background text-white p-3 rounded-full">
                  <p className="font-bold">INTERMEDIATE BACHATA</p>
                  <p>7:15-8:15PM</p>
                </div>
                <div className="bg-background text-white p-3 rounded-full relative">
                  <span className="absolute -top-2 -right-2 bg-primary text-white text-xs px-2 py-1 rounded-full">NEW!</span>
                  <p className="font-bold">ADVANCE SALSA</p>
                  <p>8:15-9:15PM</p>
                </div>
              </div>
            </div>

            {/* Friday Schedule */}
            <div className="bg-light-gray p-6 rounded-lg shadow-lg">
              <h3 className="text-2xl font-bold text-primary mb-4">FRIDAY</h3>
              <div className="mb-4">
                {isMay30th ? (
                  <>
                    <div className="bg-primary text-white px-2 py-1 rounded text-xs font-bold mb-2 inline-block">
                      TODAY: SPECIAL LOCATION
                    </div>
                    <p className="text-sm text-secondary mb-2">76 W 6TH AVE,</p>
                    <p className="text-sm text-secondary mb-4">VANCOUVER, V5Y 1K1</p>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-secondary mb-2">345 W BROADWAY,</p>
                    <p className="text-sm text-secondary mb-4">VANCOUVER, V5Y 1P8</p>
                  </>
                )}
              </div>
              
              <div className="space-y-4">
                <div className="bg-background text-white p-3 rounded-full">
                  <p className="font-bold">BEGINNERS/IMPROVERS BACHATA</p>
                  <p>7-8PM</p>
                </div>
                <div className="bg-background text-white p-3 rounded-full">
                  <p className="font-bold">BEGINNERS/IMPROVERS SALSA</p>
                  <p>8-9PM</p>
                </div>
                <div className="bg-primary text-white p-3 rounded-full">
                  <p className="font-bold">FRIDAY SOCIAL</p>
                  <p>9-10PM</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Schedule;